import { Injectable, inject } from '@angular/core';
import { classes } from 'polytype';
import type { Observable } from 'rxjs';

import type { FeedbackPortalEvent } from '@app/modules/testing/models/feedback-portal.model';
import type {
    TestRunAdapter,
    UserInputAdapter,
} from '@app/modules/testing/models/test-session.model';
import type {
    FEEDBACK_TYPE,
    InteractionAdapter,
} from '@app/shared/components/chat/model/chat.model';
import { TEST_RUN_PATH } from '@app/shared/constants/api-route.config';
import {
    type ItemResponse,
    type ListResponse,
    type StatusResponse,
    WithCrudService,
} from '@app/shared/services/mixins/crud.service';
import { RestService } from '@app/shared/services/rest.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';

@Injectable({
    providedIn: 'root',
})
export class TestRunService extends classes(WithCrudService<TestRunAdapter>) {
    protected endpoint = TEST_RUN_PATH;

    protected readonly api = inject(RestService);
    protected readonly toast = inject(ToastNotificationService);

    getDataList(
        params: Parameters<WithCrudService<TestRunAdapter>['getDataList']>[0] & {
            pipelineVersionId?: number;
            simple?: boolean;
            projectSlug?: string;
        },
    ): Observable<ListResponse<TestRunAdapter>> {
        return this.api.getWithParams(this.endpoint, params);
    }

    // FIXME: This API doesn't work right now - need testing
    postChatInteraction(
        runId: number,
        payload: UserInputAdapter,
    ): Observable<ItemResponse<InteractionAdapter[]>> {
        return this.api.post(`${this.endpoint}${runId}/chat`, payload, false);
    }

    addInteractionComment(
        interactionId: number,
        comment: string,
        issueType: string,
    ): Observable<ItemResponse<InteractionAdapter>> {
        return this.api.post(`${this.endpoint}interactions/${interactionId}/comment`, {
            comment,
            issueType,
        });
    }

    updateInteractionRating(
        interactionId: number,
        rating: FEEDBACK_TYPE,
    ): Observable<ItemResponse<InteractionAdapter>> {
        return this.api.post(`${this.endpoint}interactions/${interactionId}/rating`, {
            rating,
        });
    }

    deleteInteraction(interactionId: number): Observable<StatusResponse> {
        return this.api.delete<StatusResponse>(`${this.endpoint}interactions/${interactionId}`);
    }

    /** Get feedback portal events linked to this test run (no feedback portal id needed). */
    getEventsForRun(runId: number): Observable<ListResponse<FeedbackPortalEvent>> {
        return this.api.get<ListResponse<FeedbackPortalEvent>>(
            `${this.endpoint}${runId}/events`,
            true,
        );
    }
}
